import { BOOKING_STATUS, NON_COMPLIANT_STATUSES, type BookingStatusId } from './bookingConstants';

/**
 * Formats a CCMS timestamp to a display string. See {@link parseCcmsTimestamp}
 * for the accepted encodings.
 */
export function formatBookingDateTime(timestamp: number | decimal): string {
  const d = parseCcmsTimestamp(timestamp as number);
  return d ? d.toLocaleString() : '—';
}

/**
 * Parses a CCMS timestamp into a Date object. Returns null if invalid.
 *
 * The canonical booking encoding is a decimal `yyyyMMddHHmmss` "Num14"
 * (e.g. 20260619143000) — that's what the web create/availability paths and
 * the backend views use. However, some records were written by clients that
 * sent a raw unix epoch instead (the iOS CCMS-Booking app historically sent
 * `timeIntervalSince1970`, i.e. epoch *seconds*), which otherwise renders as
 * "21/01/1970". The three encodings occupy non-overlapping magnitude ranges,
 * so we detect and normalise each rather than mis-render:
 *   • >= 1e13 (14 digits)     -> decimal yyyyMMddHHmmss
 *   • >= 1e12 (~13 digits)    -> epoch milliseconds
 *   • >= 1e8  (~9-12 digits)  -> epoch seconds
 */
export function parseCcmsTimestamp(timestamp: number | string | null | undefined): Date | null {
  if (timestamp == null || timestamp === '') return null;
  const n = typeof timestamp === 'number' ? timestamp : Number(timestamp);
  if (!Number.isFinite(n) || n <= 0) return null;

  let d: Date;
  if (n >= 1e13) {
    // Decimal yyyyMMddHHmmss (Num14).
    const s = String(Math.trunc(n)).padStart(14, '0');
    const iso = `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}T${s.slice(8, 10)}:${s.slice(10, 12)}:${s.slice(12, 14)}`;
    d = new Date(iso);
  } else if (n >= 1e12) {
    // Unix epoch milliseconds.
    d = new Date(n);
  } else {
    // Unix epoch seconds (e.g. iOS-created bookings).
    d = new Date(n * 1000);
  }
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Converts a Date to CCMS decimal timestamp format (yyyyMMddHHmmss).
 */
export function toTimestamp(date: Date): number {
  const pad = (n: number) => String(n).padStart(2, '0');
  const str =
    `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}` +
    `${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
  return parseInt(str, 10);
}

/**
 * Checks if any bookings in the list are non-compliant (overdue or incomplete).
 */
export function hasNonCompliantBookings(bookings: Array<{ bookingStatusID?: number }>): boolean {
  return bookings.some((b) =>
    NON_COMPLIANT_STATUSES.includes(b.bookingStatusID as BookingStatusId)
  );
}

/**
 * Counts non-compliant bookings.
 */
export function countNonCompliant(bookings: Array<{ bookingStatusID?: number }>): number {
  return bookings.filter((b) =>
    NON_COMPLIANT_STATUSES.includes(b.bookingStatusID as BookingStatusId)
  ).length;
}

/**
 * Gets a display name for a booking.
 */
export function getBookingDisplayName(booking: { resourceName?: string | null; bookingID?: number }): string {
  return booking.resourceName || `Booking #${booking.bookingID}`;
}

/**
 * Sorts bookings by created date descending (newest first).
 */
export function sortByCreatedDate<T extends { createdDateTime?: number }>(bookings: T[]): T[] {
  return [...bookings].sort((a, b) => (b.createdDateTime ?? 0) - (a.createdDateTime ?? 0));
}

/**
 * Checks if a booking is in an active state (pending, approved, or active).
 */
export function isActiveBooking(statusId: number): boolean {
  return statusId === BOOKING_STATUS.PENDING ||
    statusId === BOOKING_STATUS.APPROVED ||
    statusId === BOOKING_STATUS.ACTIVE;
}

/**
 * "Good morning" / "Good afternoon" / "Good evening" / "Hi" based on the
 * given (or current) hour. Mirrors HRManagement's greetingForHour for
 * visual parity across the staff portals.
 */
export function greetingForHour(date: Date = new Date()): string {
  const hour = date.getHours();
  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 17) return 'Good afternoon';
  if (hour >= 17 && hour < 22) return 'Good evening';
  return 'Hi';
}

/**
 * Two-letter initials from a display name. "Stanley Masuku" → "SM",
 * "Stanley" → "ST", empty → "?". Same rules as hrHelpers.initials so
 * avatars look identical across HR and Booking surfaces.
 */
export function initials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
